/**
 * client/src/lib/handle.ts — "somebody arrived with a link", resolved once, for every page that
 * can be arrived at that way.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * **The bug this removes was in four pages at once and produced no error anywhere.**
 *
 * `<Claim>`, `<Live>`, `<Wait>` and `<Reveal>` each read one path segment and passed it straight to
 * `GET /api/v1/sessions/:id`. That works for the person who recorded the session — her URL has a
 * `ses_` id in it. It does **not** work for anyone arriving on a claim link, which is the entire
 * population those four pages were written for: the server's `sessionById()` refuses anything that is
 * not a session id before it queries, so `GET /api/v1/sessions/vox_…` is a flat 404.
 *
 * The result was that VOX-LAW 20's durable handle — the thing §2.4 promises still works after a
 * cleared cookie, a private window, a handed-back phone or a different browser — resolved to "not
 * ready yet" on every page it reached, with the recording sitting on disk the whole time and nothing
 * in any log to say so. `/c/:claimToken` itself was correct; everything downstream of it was not.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── Why a hook and not four copies ──────────────────────────────────────────────────────────────
 *
 * The sequence has an order the server dictates (see `resolveSession()` in `lib/api.ts`), and the
 * token has to be carried onto every later call on the page. Four hand-written copies of that is four
 * chances to drop the token on one call and get a 404 that looks like "the session is gone" — on the
 * screens whose whole job is to prove it is not.
 *
 * ⚠️ **`notFound` and a null `session` are different states and pages must not merge them.** Null is
 * "still resolving"; `notFound` is the server's answer. Rendering the "we can't find that" screen
 * while the first request is in flight is a page that accuses the link of being dead for as long as
 * the network takes — on someone who has just been told this link is how they get their mother's
 * voice back.
 */

import { useEffect, useState } from "react";

import type { SessionView } from "../../../shared/protocol.ts";
import { ApiError, describeForConsole, resolveSession } from "./api.ts";

export interface ResolvedHandle {
  /** Null while the first request is in flight, and null when it failed. Check `notFound` first. */
  session: SessionView | null;
  /** The real `ses_` id. **Use this for every later call**, never the raw path segment. */
  sessionId: string | null;
  /**
   * Present when the page was reached by a claim link. Every later call needs it: the holder is a
   * guest who is not this session's actor, so `readAccess` refuses without it.
   */
  claimToken: string | null;
  /** The server said no — or said nothing legible. VOX-LAW 18 makes those the same answer. */
  notFound: boolean;
}

/**
 * Resolve one path segment — a `ses_` id or a `vox_` claim token — to a session.
 *
 * `where` names the caller in the console line, because the same failure on `/live` and on `/back`
 * mean different things to the person debugging it.
 */
export function useSessionHandle(handle: string, where: string): ResolvedHandle {
  const [state, setState] = useState<ResolvedHandle>({
    session: null,
    sessionId: null,
    claimToken: null,
    notFound: false,
  });

  useEffect(() => {
    if (handle === "") return;
    const ac = new AbortController();

    resolveSession(handle, ac.signal).then(
      ({ session, claimToken }) => setState({ session, sessionId: session.id, claimToken, notFound: false }),
      (err: unknown) => {
        // The caller's own unmount. Reporting it as a dead link would blame the link for a page that
        // no longer exists.
        if (err instanceof DOMException && err.name === "AbortError") return;

        // VOX-LAW 18: routes refuse 404 and never 403, so "no such session", "revoked" and "not
        // yours" arrive as one answer and must be treated as one. Anything else is a transport or a
        // contract failure, which is not hers to see either — it goes to the console with the detail.
        if (!(err instanceof ApiError && err.notFound)) {
          const d = describeForConsole(err);
          console.error(`${where}: resolve failed at ${d.where}: ${d.message}`);
        }
        setState({ session: null, sessionId: null, claimToken: null, notFound: true });
      },
    );

    return () => ac.abort();
  }, [handle, where]);

  return state;
}
